import type { ColumnDef, RowData } from '@tanstack/react-table';
import { Box } from 'hephaestus/web';
import { CheckboxField } from '../checkboxField/checkboxField';
import type { DataTableColumnMeta } from './dataTable.types';

export type CreateDataTableSelectColumnOptions = {
  /**
   * Checkbox control size. Default **small**; use **medium** for larger hit targets.
   */
  checkboxSize?: 'small' | 'medium';
  /**
   * Prefix for `data-testid` on header and row checkboxes:
   * `{prefix}-select-all`, `{prefix}-row-{rowId}`.
   */
  testIdPrefix?: string;
  /**
   * Optional visible label on the header “select all” checkbox.
   * Ignored when {@link enableSelectAll} is false.
   */
  selectAllLabel?: string;
  /**
   * When true, renders a header “select all” checkbox. Default **false** so the
   * header cell stays empty (row checkboxes only).
   */
  enableSelectAll?: boolean;
  /**
   * Column width for the selection column (number = pixels). Applied via
   * `meta.dataTable.width` so **DataTableView** sizes the cell.
   */
  columnWidth?: number | string;
  /** Optional `minWidth` / `maxWidth` on `meta.dataTable` (number = pixels). */
  minColumnWidth?: number | string;
  maxColumnWidth?: number | string;
  /**
   * Tighter cell padding for a narrow checkbox column. Default **true**.
   * Set **false** to use the table’s default padding.
   */
  compact?: boolean;
};

/**
 * Returns a TanStack **display** column (checkbox header + row cells) wired to
 * {@link useDataTable}’s `enableRowSelection` / `rowSelection`. Prepend to your
 * `columns` array. Requires stable row identity: pass `getRowId` into the hook
 * when rows can be reordered or refetched.
 */
export function createDataTableSelectColumn<TData extends RowData>(
  options: CreateDataTableSelectColumnOptions = {},
): ColumnDef<TData, unknown> {
  const {
    checkboxSize = 'small',
    testIdPrefix,
    selectAllLabel,
    enableSelectAll = false,
    columnWidth: columnWidthOption,
    minColumnWidth,
    maxColumnWidth,
    compact = true,
  } = options;

  const columnWidth =
    columnWidthOption ?? (checkboxSize === 'medium' ? 56 : 48);

  const columnMeta: DataTableColumnMeta = {
    align: 'center',
    disableDataTableSortHandling: true,
    isFirst: true,
    width: columnWidth,
    ...(minColumnWidth !== undefined ? { minWidth: minColumnWidth } : {}),
    ...(maxColumnWidth !== undefined ? { maxWidth: maxColumnWidth } : {}),
    compact,
    ...(enableSelectAll ? { headerBare: true } : {}),
  };

  return {
    id: 'select',
    enableSorting: false,
    enableHiding: false,
    meta: { dataTable: columnMeta },
    header: ({ table }) =>
      enableSelectAll ? (
        <Box
          component='span'
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
        >
          <CheckboxField
            size={checkboxSize}
            {...(selectAllLabel != null && selectAllLabel !== ''
              ? { label: selectAllLabel }
              : {})}
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={(checked) => {
              table.toggleAllRowsSelected(checked);
            }}
            data-testid={
              testIdPrefix !== undefined
                ? `${testIdPrefix}-select-all`
                : undefined
            }
          />
        </Box>
      ) : null,
    cell: ({ row }) => (
      <Box
        component='span'
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
      >
        <CheckboxField
          size={checkboxSize}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={(checked) => {
            row.toggleSelected(checked);
          }}
          data-testid={
            testIdPrefix !== undefined
              ? `${testIdPrefix}-row-${row.id}`
              : undefined
          }
        />
      </Box>
    ),
  };
}
